/**
 * PageSpinner — premium loading indicator for auth-guarded routes.
 *
 * Design: "Orbital Precision" — scholarly restraint meets architectural precision.
 * Two concentric arcs rotate in opposite directions, evoking the motion of pages
 * and the passage of time in a literary tradition. Warm brand gold accent over
 * a neutral track ring.
 *
 * Motion: exponential ease-out (ease-out-quint), counter-rotating rings at
 * different speeds create an organic, non-mechanical feel.
 * Respects `prefers-reduced-motion` via opacity fade.
 *
 * Positioning: self-centering via CSS grid on the wrapper span — works
 * regardless of parent shell layout (AppShell, AdminShell, or bare element).
 * All auth-guarded routes use this single spinner; one change propagates everywhere.
 */
import { type CSSProperties } from 'react';

interface PageSpinnerProps {
  /** Size in logical pixels (diameter). Default 40. */
  size?: number;
  /** Additional CSS class. */
  className?: string;
  /**
   * Fill the full viewport and self-center.
   * Use when spinner replaces a shell (auth loading) rather than appearing inside one.
   * Default: false.
   */
  fullScreen?: boolean;
}

const TRACK_COLOR = 'oklch(0.88 0.01 80)';
const ARC_COLOR = 'oklch(0.72 0.15 85)';
const EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';

export function PageSpinner({ size = 40, className, fullScreen = false }: PageSpinnerProps) {
  const ring = size;
  const outer = size;
  const inner = size * 0.6;

  /** Wrapper: self-centering grid. When fullScreen, fills the full viewport. */
  const wrapperStyle: CSSProperties = fullScreen
    ? { display: 'grid', placeItems: 'center', width: '100%', minHeight: '100vh' }
    : { display: 'grid', placeItems: 'center', width: '100%', height: '100%', minHeight: ring };

  const style: CSSProperties = {
    width: ring,
    height: ring,
    position: 'relative',
    display: 'block',
    flexShrink: 0,
  };

  return (
    <span
      role="status"
      aria-label="Loading"
      className={className}
      style={wrapperStyle}
    >
      <span style={style}>
        {/* Outer track ring */}
        <svg
          width={outer}
          height={outer}
          viewBox={`0 0 ${outer} ${outer}`}
          style={{ position: 'absolute', inset: 0 }}
          aria-hidden="true"
        >
          <circle
            cx={outer / 2}
            cy={outer / 2}
            r={outer / 2 - 1.5}
            fill="none"
            stroke={TRACK_COLOR}
            strokeWidth={1.5}
            className="dark:stroke-[--color-surface-highest]"
          />
        </svg>

        {/* Outer arc — rotates clockwise */}
        <svg
          width={outer}
          height={outer}
          viewBox={`0 0 ${outer} ${outer}`}
          style={{ position: 'absolute', inset: 0, animation: `spinnerOuterRotate 1.6s ${EASE} infinite` }}
          aria-hidden="true"
        >
          <circle
            cx={outer / 2}
            cy={outer / 2}
            r={outer / 2 - 1.5}
            fill="none"
            stroke={ARC_COLOR}
            strokeWidth={2}
            strokeLinecap="round"
            strokeDasharray={`${outer * 0.45} ${outer * 2}`}
            strokeDashoffset={outer * 0.15}
            className="dark:stroke-[--color-brand-accent]"
          />
        </svg>

        {/* Inner track ring */}
        <svg
          width={inner}
          height={inner}
          viewBox={`0 0 ${inner} ${inner}`}
          style={{ position: 'absolute', inset: '20%' }}
          aria-hidden="true"
        >
          <circle
            cx={inner / 2}
            cy={inner / 2}
            r={inner / 2 - 1}
            fill="none"
            stroke={TRACK_COLOR}
            strokeWidth={1}
            className="dark:stroke-[--color-surface-highest]"
          />
        </svg>

        {/* Inner arc — rotates counter-clockwise at faster speed */}
        <svg
          width={inner}
          height={inner}
          viewBox={`0 0 ${inner} ${inner}`}
          style={{ position: 'absolute', inset: '20%', animation: `spinnerInnerRotate 1.0s ${EASE} infinite reverse` }}
          aria-hidden="true"
        >
          <circle
            cx={inner / 2}
            cy={inner / 2}
            r={inner / 2 - 1}
            fill="none"
            stroke={ARC_COLOR}
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeDasharray={`${inner * 0.5} ${inner * 2}`}
            strokeDashoffset={inner * 0.1}
            className="dark:stroke-[--color-brand-accent]"
            opacity={0.65}
          />
        </svg>

        <style>{`
          @keyframes spinnerOuterRotate {
            from { transform: rotate(0deg); }
            to   { transform: rotate(360deg); }
          }
          @keyframes spinnerInnerRotate {
            from { transform: rotate(0deg); }
            to   { transform: rotate(360deg); }
          }
          @media (prefers-reduced-motion: reduce) {
            [role="status"] svg:last-of-type,
            [role="status"] svg:nth-last-child(2) {
              animation-duration: 3s;
            }
          }
        `}</style>
      </span>
    </span>
  );
}

/** Convenience fallback for inline contexts where JSX isn't available. */
export const Spinner = () => <PageSpinner />;
